import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ slug: "", label: "", order: 100 });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/categories", {
        slug: form.slug.trim().toLowerCase(), label: form.label.trim(), order: parseInt(form.order, 10) || 100,
      });
      toast.success(`Category ${form.label} added`);
      setForm({ slug: "", label: "", order: 100 });
      load();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Create failed");
    }
    setSaving(false);
  };

  const updateLabel = async (slug, label) => {
    try { await api.put(`/admin/categories/${slug}`, { label }); toast.success("Saved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete category "${slug}"? Products using it must be moved first.`)) return;
    try { await api.delete(`/admin/categories/${slug}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div data-testid="admin-categories">
      <p className="mb-6 max-w-xl text-white/50">
        Categories power the tabs on the Store page. Add new ones (e.g. <span className="font-mono text-white/70">bundle</span>, <span className="font-mono text-white/70">unlocks</span>) and assign products to them in the Products tab.
      </p>

      <form onSubmit={create} className="rounded-2xl border border-white/15 p-6 grid gap-4 sm:grid-cols-4">
        <div>
          <label className="font-mono text-[10px] tracking-[0.2em] text-white/40">SLUG (a-z, -)</label>
          <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required placeholder="bundle"
            data-testid="new-cat-slug"
            className="mt-2 w-full bg-transparent border border-white/15 px-3 py-3 text-sm font-mono focus:border-white focus:outline-none" />
        </div>
        <div>
          <label className="font-mono text-[10px] tracking-[0.2em] text-white/40">LABEL</label>
          <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} required placeholder="BUNDLES"
            data-testid="new-cat-label"
            className="mt-2 w-full bg-transparent border border-white/15 px-3 py-3 text-sm focus:border-white focus:outline-none" />
        </div>
        <div>
          <label className="font-mono text-[10px] tracking-[0.2em] text-white/40">ORDER</label>
          <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })}
            data-testid="new-cat-order"
            className="mt-2 w-full bg-transparent border border-white/15 px-3 py-3 text-sm font-mono focus:border-white focus:outline-none" />
        </div>
        <div className="flex items-end">
          <button type="submit" disabled={saving} data-testid="new-cat-submit" className="btn-inj w-full py-3 text-xs inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} ADD
          </button>
        </div>
      </form>

      <div className="mt-8 rounded-2xl overflow-hidden border border-white/10" data-testid="admin-cats-list">
        <div className="grid grid-cols-[1fr_1fr_auto_auto] gap-3 px-5 py-3 border-b border-white/10 font-mono text-[10px] tracking-[0.2em] text-white/40">
          <span>SLUG</span><span>LABEL</span><span>ORDER</span><span></span>
        </div>
        {cats.map((c) => <CategoryRow key={c.slug} c={c} onUpdate={updateLabel} onDelete={remove} />)}
        {cats.length === 0 && <div className="px-5 py-10 text-center font-mono text-xs tracking-[0.2em] text-white/40">// NO CATEGORIES</div>}
      </div>
    </div>
  );
}

function CategoryRow({ c, onUpdate, onDelete }) {
  const [label, setLabel] = useState(c.label);
  return (
    <div className="grid grid-cols-[1fr_1fr_auto_auto] gap-3 items-center px-5 py-3 border-b border-white/[0.06]">
      <span className="font-mono text-sm">{c.slug}</span>
      <input value={label} onChange={(e) => setLabel(e.target.value)}
        data-testid={`cat-label-${c.slug}`}
        className="bg-transparent border border-white/12 px-3 py-2 text-sm focus:border-white focus:outline-none" />
      <span className="font-mono text-xs text-white/50">{c.order}</span>
      <div className="flex items-center gap-2">
        <button onClick={() => onUpdate(c.slug, label)} data-testid={`cat-save-${c.slug}`} className="text-white/60 hover:text-white" title="Save"><Save size={16} /></button>
        <button onClick={() => onDelete(c.slug)} data-testid={`cat-del-${c.slug}`} className="text-white/60 hover:text-white" title="Delete"><Trash2 size={16} /></button>
      </div>
    </div>
  );
}
